<template>
  <panel>
    <template #title>
      Insights
    </template>

    <template #controls>
      <period-selector
        :company-created-at="companyCreatedAt"
        :selected-month="selectedMonth"
        @selected-month="selectedMonth = $event"
        :type="periodType"
        @type="periodType = $event"
        :selected-year="selectedYear"
        @selected-year="selectedYear = $event"
      />
    </template>
    <small class="tw-text-gray-600">Informações atualizadas a cada 10 minutos</small>

    <div class="insights-blocks-container">
      <div class="insights-block">
        <tooltip :tooltip="tooltips.attendance" />
        <p class="insights-block__name">Percentual de comparecimento</p>
        <p v-if="insights.attendance.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <div v-if="insights.attendance.fetched && insights.attendance.percent" class="tw-flex tw-flex-1 tw-flex-col tw-justify-between">
          <p :class="`insights-block__attendance-level insights-block__attendance-level-${attendanceStats.attendanceLevel.level}`">{{attendanceStats.attendanceLevel.text}}</p>
          <p class="insights-block__primary-number">{{String(insights.attendance.percent).replace(".", ",")}}%</p>
          <p :class="`insights-block__average-level insights-block__average-level-${attendanceStats.averageLevel}`">
            {{attendanceStats.averageText}}
          </p>
        </div>
        <div v-if="insights.attendance.fetched && insights.attendance.percent === null" class="tw-flex tw-flex-1 tw-flex-col tw-justify-between">
          <p class="insights-block__primary-number">-</p>
        </div>
      </div>

      <div class="insights-block">
        <tooltip :tooltip="tooltips.appointments" />
        <p class="insights-block__name">Consultas agendadas</p>
        <p v-if="insights.appointments.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>

        <p v-if="insights.appointments.fetched" class="insights-block__primary-number">{{insights.appointments.current_period}}</p>
        <indicator v-if="insights.appointments.fetched" :period-type="periodType" :percentage="insights.appointments.percentage" />
      </div>

      <div class="insights-block">
        <tooltip :tooltip="tooltips.appointments_canceled" />
        <p class="insights-block__name">Consultas canceladas</p>
        <p v-if="insights.appointments_canceled.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.appointments_canceled.fetched"  class="insights-block__primary-number">{{insights.appointments_canceled.current_period}}</p>
        <indicator v-if="insights.appointments_canceled.fetched"  :period-type="periodType" :percentage="insights.appointments_canceled.percentage" :bigger-is-better="false"/>
      </div>

      <div class="insights-block">
        <tooltip :tooltip="tooltips.new_patients" />
        <p class="insights-block__name">Novos pacientes</p>
        <p v-if="insights.new_patients.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.new_patients.fetched" class="insights-block__primary-number">{{insights.new_patients.current_period}}</p>
        <indicator v-if="insights.new_patients.fetched" :period-type="periodType" :percentage="insights.new_patients.percentage" />
      </div>

      <div class="insights-block">
        <p class="insights-block__name">Profissional que mais atendeu</p>
        <p v-if="insights.professional_with_more_appointments.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.professional_with_more_appointments.fetched" class="insights-block__primary-number">{{insights.professional_with_more_appointments.professional_name}}</p>
        <div v-if="insights.professional_with_more_appointments.fetched" class="tw-invisible">-</div>
      </div>

      <div class="insights-block">
        <p class="insights-block__name">Documentos assinados</p>
        <p v-if="insights.signed_documents.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.signed_documents.fetched" class="insights-block__primary-number">{{insights.signed_documents.current_period}}</p>
        <indicator v-if="insights.signed_documents.fetched" :period-type="periodType" :percentage="insights.signed_documents.percentage" />
      </div>

      <div class="insights-block">
        <p class="insights-block__name">Retornos marcados</p>
        <p v-if="insights.return_appointments.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.return_appointments.fetched" class="insights-block__primary-number">{{insights.return_appointments.current_period}}</p>
        <indicator v-if="insights.return_appointments.fetched" :period-type="periodType" :percentage="insights.return_appointments.percentage" />
      </div>

      <div class="insights-block">
        <p class="insights-block__name">Orçamentos fechados</p>
        <p v-if="insights.approved_quotes.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.approved_quotes.fetched" class="insights-block__primary-number">{{insights.approved_quotes.current_period}}</p>
        <indicator v-if="insights.approved_quotes.fetched" :period-type="periodType" :percentage="insights.approved_quotes.percentage" />
      </div>

      <div class="insights-block">
        <p class="insights-block__name">Dia com mais atendimento</p>
        <p v-if="insights.day_with_more_appointments.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.day_with_more_appointments.fetched" class="insights-block__primary-number">{{translateDayOfWeek(insights.day_with_more_appointments.day)}}</p>
        <div v-if="insights.day_with_more_appointments.fetched" class="tw-invisible">-</div>
      </div>

      <div class="insights-block">
        <p class="insights-block__name">Convênio mais utilizado</p>
        <p v-if="insights.dental_plan_more_appointments.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.dental_plan_more_appointments.fetched" class="insights-block__primary-number">
          {{insights.dental_plan_more_appointments.dental_plan_name}}
        </p>
        <p v-if="insights.dental_plan_more_appointments.fetched && insights.dental_plan_more_appointments.dental_plan_name == null " class="insights-block__primary-number">
          -
        </p>

        <p v-if="insights.dental_plan_more_appointments.fetched && insights.dental_plan_more_appointments.appointments != null" class="tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-mt-4 tw-mx-auto tw-p-1 tw-px-2 tw-text-gray-800 tw-w-full tw-rounded-br tw-rounded-bl">
          {{insights.dental_plan_more_appointments.appointments}} consultas
        </p>
      </div>

      <div class="insights-block">
        <p class="insights-block__name">Tempo espera</p>
        <p v-if="insights.waiting_duration.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.waiting_duration.fetched && insights.waiting_duration.current_period > 120" class="insights-block__primary-number">{{formatDurationTimeSeconds(insights.waiting_duration.current_period)}}</p>
        <p v-if="insights.waiting_duration.current_period < 120" class="insights-block__primary-number">-</p>

        <indicator v-if="insights.waiting_duration.fetched" :period-type="periodType" :percentage="insights.waiting_duration.current_period < 120 ? 0 : insights.waiting_duration.percentage" :bigger-is-better="false"/>
      </div>

      <div class="insights-block">
        <p class="insights-block__name">Tempo atendimento</p>
        <p v-if="insights.attendance_duration.loading">
          <img v-bind:src="loadingImage" class="tw-mx-auto tw-my-8" >
        </p>
        <p v-if="insights.attendance_duration.fetched && insights.attendance_duration.current_period > 120" class="insights-block__primary-number">{{formatDurationTimeSeconds(insights.attendance_duration.current_period)}}</p>
        <p v-if="insights.attendance_duration.current_period < 120" class="insights-block__primary-number">-</p>
        <indicator v-if="insights.attendance_duration.fetched" :period-type="periodType" :percentage="insights.attendance_duration.current_period < 120 ? 0 : insights.attendance_duration.percentage" :bigger-is-better="false"/>
      </div>
    </div>

    <absence-chart
      :selected-month="selectedMonth"
      :period-type="periodType"
      :selected-year="selectedYear"
    />
  </panel>
</template>

<script>
import Panel from "../panel"
import axios from "axios";
import PeriodSelector from "../form/period-selector"
import Indicator from "./indicator"
import AbsenceChart from "./absence-chart"
import Tooltip from "./tooltip.vue"
import {translateDayOfWeek} from "../../helpers/translateDayOfWeek"
import {getAttendanceLevel} from "../../helpers/getAttendanceLevel"
import loadingImage from 'images/spinner.svg';
import {formatDurationTimeSeconds} from "../../helpers/formatDurationTime"

const insightsBase = {
  attendance: {
    loading: true,
    fetched: false,
  },
  appointments: {
    loading: true,
    fetched: false,
  },
  appointments_canceled: {
    loading: true,
    fetched: false,
  },
  new_patients: {
    loading: true,
    fetched: false,
  },
  professional_with_more_appointments: {
    loading: true,
    fetched: false,
  },
  signed_documents: {
    loading: true,
    fetched: false,
  },
  return_appointments: {
    current_period: null,
    loading: true,
    fetched: false,
  },
  approved_quotes: {
    loading: true,
    fetched: false,
  },
  day_with_more_appointments: {
    loading: true,
    fetched: false,
  },
  dental_plan_more_appointments: {
    loading: true,
    fetched: false,
  },
  attendance_duration: {
    loading: true,
    fetched: false,
  },
  waiting_duration: {
    loading: true,
    fetched: false,
  },
}

const tooltips = {
  attendance: `A avaliação de comparecimento é feita com base em mais de 3 mil clínicas.<br>
  Muito ruim: menor 65% <br>
  Ruim: entre 65% - 70% <br>
  Regular: entre 70% - 75% <br>
  Bom: entre 75% - 80% <br>
  Muito bom: entre 80% - 85% <br>
  Excelente: maior 85%`,
  appointments: `Um apanhado de todas as consultas com status diferente de "Faltou", "Cancelada pelo paciente" e "Cancelada pelo dentista".`,
  appointments_canceled: `Todas as consultas com status de "Faltou", "Cancelada pelo paciente" e "Cancelada pelo dentista".`,
  new_patients: `Pacientes que entraram para sua base no intervalo definido.`,
  attendance_duration: "Tempo médio de espera ",
  waiting_duration: "",
}

export default {
  components: {
    Panel,
    PeriodSelector,
    Indicator,
    AbsenceChart,
    Tooltip,
  },
  props: {
    companyCreatedAt: String,
  },
  data(){
    const insightsKeys = Object.keys(insightsBase)
    const prevMonth = moment().subtract(1,'months').startOf("month")
    let selectedMonth = moment().month() + 1
    let selectedYear = moment().year()

    if (moment().month() < 3 && moment(this.companyCreatedAt)._d < prevMonth._d) {
      selectedMonth = prevMonth.month() + 1
      selectedYear = prevMonth.year()
    }

    return {
      loadingImage,
      loaded: false,
      loading: true,
      selectedMonth,
      selectedYear,
      periodType: "all",
      insights: insightsBase,
      insightsKeys,
      insightsToFetch: insightsKeys.length,
      establishment: {
        boletos_enabled: null,
        appointment_reminder_using_whatsapp_and_sms: null,
        debts: null,
        active_campaigns: null,
        number_of_messages: null,
        send_reminder_hours_before_the_appointment: null,
      },
      tooltips
    }
  },
  mounted() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.loading = true
      this.insights = {...insightsBase}
      this.insightsToFetch = this.insightsKeys.length

      const params = {
        month: this.selectedMonth,
        year: this.selectedYear,
        period_type: this.periodType,
      }

      axios
        .get(
          '/insights/statistics', {params}
        )
        .then(response => {
          const data = response.data
          const insights = data.insights
          if (insights !== null) {
            this.insightsKeys.map((insightName, index) => {
              if (insights[insightName]) {
                const insight = insights[insightName]
                if (insight.current_period && insight.previous_period) {
                  insight.percentage = (insight.current_period - insight.previous_period) / insight.previous_period * 100
                  insight.percentage = parseFloat(insight.percentage.toFixed(2))
                }
                this.insightsToFetch--;
                this.insights[insightName] = {
                  loading: false,
                  fetched: true,
                  ...insight
                }
              }
            });
          }
          if (this.insightsToFetch > 0) {
            this.fetchInsight()
          }
          this.establishment.boletos_enabled = data.boletos_enabled
          this.establishment.appointment_reminder_using_whatsapp_and_sms = data.appointment_reminder_using_whatsapp_and_sms
          this.establishment.debts = data.debts
          this.establishment.active_campaigns = data.active_campaigns
          this.establishment.number_of_messages = data.number_of_messages
          this.establishment.send_reminder_hours_before_the_appointment = data.send_reminder_hours_before_the_appointment
          this.loaded = true
          this.loading = false
        });
        // TODO add error handling
    },
    fetchInsight(){
      if (this.insightsToFetch <= 0) {
        return false
      }

      const insightName = this.insightsKeys.find((insightName) => {
        return this.insights[insightName].fetched === false
      });

      if (!insightName) {
        return false;
      }

      const params = {
        month: this.selectedMonth,
        year: this.selectedYear,
        period_type: this.periodType,
        insight: insightName,
      }
      axios
        .get(
          '/insights/statistics', {params}
        )
        .then(response => {
          const insight = response.data.insight
          this.insightsToFetch--;

          if (insight.current_period && insight.previous_period) {
            insight.percentage = (insight.current_period - insight.previous_period) / insight.previous_period * 100
            insight.percentage = parseFloat(insight.percentage.toFixed(2))
          }

          this.insights[insightName] = {
            loading: false,
            fetched: true,
            ...insight
          }
          this.fetchInsight()
          this.loading = false
        });
    },
    translateDayOfWeek(dow){
      return translateDayOfWeek(dow);
    },
    getAttendanceLevel(percentage){
      return getAttendanceLevel(percentage);
    },
    formatDurationTimeSeconds(durationInMinutes){
      return formatDurationTimeSeconds(durationInMinutes)
    }
  },
  computed: {
    attendanceStats(){
      const AVERAGE = 85
      const difference = Math.abs(parseInt(this.insights.attendance.percent - AVERAGE))
      const attendanceLevel = getAttendanceLevel(this.insights.attendance.percent)
      let averageLevel = "bad";
      averageLevel = this.insights.attendance.percent >= 70 ? "normal" : averageLevel
      averageLevel = this.insights.attendance.percent >= 75 ? "good" : averageLevel

      return {
        averageText: `Objetivo > 85%`,
        averageLevel,
        attendanceLevel,
      }
    }
  },
  watch: {
    selectedMonth(){
      this.fetch();
    },
    selectedYear(){
      this.fetch();
    },
    periodType(){
      this.fetch();
    },
    "insights.attendance"(){
      if (!this.insights.attendance.fetched){
        return
      }

      const attendancePercent = this.insights.attendance.percent
      this.$emit(attendancePercent < 75 ? "add-hint" : "remove-hint", {
        id: "attendance_percent_lower_than_75",
        text: "Sua taxa de comparecimento é baixa em relação as demais clínicas cadastradas. Você precisa melhorar esse número"
      });

      this.$emit(attendancePercent < 85 && !this.establishment.appointment_reminder_using_whatsapp_and_sms ? "add-hint" : "remove-hint", {
        id: "attendance_percent_lower_than_85_and_no_reminders",
        text: "Ative as notificações de consulta via WhatsApp e aumente sua taxa de comparecimento"
      });

      this.$emit(attendancePercent < 85 && this.establishment.appointment_reminder_using_whatsapp_and_sms && !this.establishment.send_reminder_hours_before_the_appointment ? "add-hint" : "remove-hint", {
        id: "attendance_reminder_hours_before",
        text: "Ative os lembretes 2h antes da consulta e aumente sua taxa de comparecimento"
      });

      this.$emit(this.establishment.appointment_reminder_using_whatsapp_and_sms && this.establishment.number_of_messages < 10 ? "add-hint" : "remove-hint", {
        id: "attendance_lower_messages",
        text: "Mantenha sua conta sempre com créditos de WhatsApp, assim você confirma todas as consultas sem se preocupar."
      });
    },
    "establishment.active_campaigns"(){
      if (!this.establishment.fetched){
        return
      }
      this.$emit(this.establishment.active_campaigns === 0 ? "add-hint" : "remove-hint", {
        id: "active_campaigns",
        text: "Você não utiliza os recursos de campanhas no Codental. <br> Campanhas de aniversariantes e retornos semestrais são ótimos recursos para reestabelecer contato com pacientes antigos."
      });
    },
    "establishment.debts"(){
      if (this.establishment.debts === null){
        return
      }
      this.$emit(this.establishment.debts === 0 ? "add-hint" : "remove-hint", {
        id: "debts",
        text: "Parece que você não utiliza os recursos do financeiro. <br> Manter todos os dados centralizados no Codental é fundamental para te ajudarmos a elaborar estratégias de crescimento. Em caso de dúvidas, chame no chat e te ajudamos a começar"
      });
    },
    "establishment.boletos_enabled"(){
      if (this.establishment.boletos_enabled === null){
        return
      }
      this.$emit(!this.establishment.boletos_enabled ? "add-hint" : "remove-hint", {
        id: "boleto",
        text: "Você não ativou a emissão de boletos bancários. <br> Experimente emitir alguns boletos pelo Codental. Além de fácil e barato, você conta com a praticidade da baixa automática do pagamento"
      });
    },
    "insights.return_appointments"(){
      if (!this.insights.return_appointments.fetched){
        return
      }
      this.$emit(this.insights.return_appointments?.current_period === 0 ? "add-hint" : "remove-hint", {
        id: "return_appointments",
        text: "Você não utiliza a ferramenta de retornos. <br> Agendar retornos pelo Codental é a forma mais fácil de manter um fluxo constante de pacientes na sua clínica. Se tiver dúvidas, chame nossa equipe no chat."
      });
    }
  },
};
</script>
